import { useState } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { CommunityId } from '../model/community.types';
import { PostId, ReactionType } from '../model/post.types';
import { Reply } from '../model/reply.types';
import { formatPostDate } from '../lib/formatPostDate';
import { useToggleReplyReaction } from '../api/useToggleReplyReaction';
import { MentionText } from './MentionText';
import { PostReactions } from './PostReactions';
import { ReplyActionsMenu } from './ReplyActionsMenu';

type Props = {
  reply: Reply;
  communityId: CommunityId;
  postId: PostId;
  influencerId?: string | number;
  canModerate: boolean;
  isDmFeatureEnabled: boolean;
};

export const ReplyCell = ({
  reply,
  communityId,
  postId,
  influencerId,
  canModerate,
  isDmFeatureEnabled,
}: Props) => {
  const toggleReplyReaction = useToggleReplyReaction(communityId);
  const [revealed, setRevealed] = useState(!reply.isSensitive);
  const { author } = reply;
  const showMenu = canModerate || author.isOwn;

  return (
    <div className="mb-3 flex gap-2">
      {author.avatarUrl ? (
        <img
          src={author.avatarUrl}
          alt={author.name}
          className="size-8 shrink-0 self-end rounded-full object-cover"
        />
      ) : (
        <div
          aria-label={author.name}
          className="flex size-8 shrink-0 self-end items-center justify-center rounded-full bg-[#D9D9D9] text-white"
        >
          <FontAwesomeIcon icon={faUser} />
        </div>
      )}
      <div
        className={`min-w-0 flex-1 rounded-[24px] px-4 py-3 ${
          author.isOwn ? 'bg-[#E8EEFF]' : 'bg-[#F3F3F6]'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-1">
            <Typography
              as="span"
              variant="body"
              size="small"
              fontWeight="bold"
              className="text-black"
            >
              {author.name}
            </Typography>
            {author.isCreator && !author.isOwn && (
              <FontAwesomeIcon icon={faBadgeCheck} className="text-[#5F1ECC]" />
            )}
          </div>
          {showMenu && (
            <ReplyActionsMenu
              reply={reply}
              communityId={communityId}
              postId={postId}
              influencerId={influencerId}
              canModerate={canModerate}
              isDmFeatureEnabled={isDmFeatureEnabled}
            />
          )}
        </div>
        {revealed ? (
          <>
            <MentionText
              as="p"
              variant="body"
              size="small"
              className="break-words"
              content={reply.content}
            />
            <div className="mt-2 flex items-end justify-between">
              <PostReactions
                reactions={reply.reactions}
                onToggle={(type: ReactionType) =>
                  toggleReplyReaction(reply.id, type)
                }
              />
              <span className="self-end text-xs text-[#8F94A1]">
                {formatPostDate(reply.createdAt)}
              </span>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="text-sm text-[#5F1ECC]"
          >
            Sensitive content - tap to reveal
          </button>
        )}
      </div>
    </div>
  );
};
